import { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "../../components/layout/AdminLayout";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Link as LinkIcon,
  Package,
  Loader2,
  Search,
  Heart,
  Sparkles,
  AlertTriangle,
  PencilLine,
} from "lucide-react";

import { SUPPORTED_MARKETPLACES } from '../../config/marketplaces';
import {
  extractApiErrorMessage,
  formatMoney,
  matchesSearch,
  normalizeAdminProducts,
} from "../../lib/safeData";
import { PRODUCT_STATUSES, submitProduct } from "../../lib/productSubmission";
import { AdminUrlSubmitAction } from "../../components/admin/AdminUrlSubmitModal";

const marketplaces = SUPPORTED_MARKETPLACES.filter((m) => m.id !== 'other').map((m) => m.id);
const categories = ["Electronics", "Fashion", "Sports", "Home", "Beauty", "Toys"];

const initialProductForm = {
  name: "",
  description: "",
  source_url: "",
  marketplace: "amazon",
  source_price: "",
  source_currency: "USD",
  category: "Electronics",
  weight_lbs: "",
  estimated_weight_lbs: "",
  images: [],
  in_stock: true,
  status: "active",
  profit_margin: "",
};

const initialManualForm = {
  name: "",
  description: "",
  source_price: "",
  profit_margin: "",
  status: "active",
  estimated_weight_lbs: "",
  marketplace: "other",
  category: "Electronics",
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(initialProductForm);
  const [saving, setSaving] = useState(false);
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [scraping, setScraping] = useState(false);
  const [lovedProducts, setLovedProducts] = useState([]);
  const [lovedLoading, setLovedLoading] = useState(true);
  const [importingId, setImportingId] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [costsRedacted, setCostsRedacted] = useState(false);
  const [manualForm, setManualForm] = useState(initialManualForm);
  const [manualSaving, setManualSaving] = useState(false);
  const [lovedAvailable, setLovedAvailable] = useState(true);

  useEffect(() => {
    fetchProducts();
    fetchLovedProducts();
  }, []);

  const fetchLovedProducts = async () => {
    setLovedLoading(true);
    try {
      const response = await axios.get(`${API}/admin/products/loved`, {
        params: { limit: 12 },
        withCredentials: true,
      });
      setLovedProducts(normalizeAdminProducts(response.data));
      setLovedAvailable(true);
    } catch (error) {
      // The engagement ledger lives on the DHC hub, so this feed is optional.
      // Hide the panel rather than showing an empty, unusable section.
      console.debug("Loved products feed unavailable:", extractApiErrorMessage(error));
      setLovedProducts([]);
      setLovedAvailable(false);
    } finally {
      setLovedLoading(false);
    }
  };

  const handleImportToShowcase = async (product) => {
    setImportingId(product.product_id);
    try {
      const response = await axios.post(
        `${API}/admin/products/showcase`,
        { product_id: product.product_id },
        { withCredentials: true }
      );
      toast.success(response.data.message || "Added to showcase");
      if (response.data.catalog_rotation?.message) {
        toast.info(response.data.catalog_rotation.message, { duration: 8000 });
      }
      fetchProducts();
      fetchLovedProducts();
    } catch (error) {
      console.error("Showcase import error:", error);
      toast.error(error.response?.data?.detail || "Failed to add to showcase");
    } finally {
      setImportingId(null);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    setLoadError("");
    try {
      // The admin endpoint returns unredacted documents. The public /products
      // endpoint strips source_price/source_url, so it is only a read-only
      // fallback used when the admin route is unavailable.
      let rows;
      let redacted = false;
      try {
        const adminResponse = await axios.get(`${API}/admin/products/all`, {
          params: { limit: 100 },
          withCredentials: true,
        });
        rows = normalizeAdminProducts(adminResponse.data);
      } catch (adminError) {
        console.warn(
          "Admin product feed unavailable, falling back to public catalog:",
          extractApiErrorMessage(adminError)
        );
        const publicResponse = await axios.get(`${API}/products`, {
          params: { limit: 100 },
        });
        rows = normalizeAdminProducts(publicResponse.data);
        redacted = rows.some((product) => product.source_price === null);
      }

      setProducts(rows);
      setCostsRedacted(redacted);
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
      setLoadError(extractApiErrorMessage(error, "Failed to load products"));
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e) => {
    const urls = e.target.value.split("\n").filter((url) => url.trim());
    setFormData({ ...formData, images: urls });
  };

  const handleScrapeUrl = async () => {
    if (!scrapeUrl) return;
    setScraping(true);
    try {
      const response = await axios.post(
        `${API}/admin/products/scrape-url`,
        null,
        {
          params: { url: scrapeUrl },
          withCredentials: true,
        }
      );
      setFormData({
        ...formData,
        ...response.data,
        source_price: response.data.source_price?.toString() || "",
        weight_lbs: response.data.weight_lbs?.toString() || "",
        estimated_weight_lbs: response.data.estimated_weight_lbs?.toString() || "1.0",
      });
      toast.success("URL scraped successfully. Please review the data.");
    } catch (error) {
      console.error("Scraping error:", error);
      toast.error("Failed to scrape URL");
    } finally {
      setScraping(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const result = await submitProduct(formData, {
      apiBase: API,
      productId: editingProduct?.product_id || null,
    });

    setSaving(false);

    if (!result.success) {
      toast.error(result.error);
      return;
    }

    toast.success(editingProduct ? "Product updated" : "Product created");
    setDialogOpen(false);
    setEditingProduct(null);
    setFormData(initialProductForm);
    setScrapeUrl("");
    fetchProducts();
  };

  const handleManualChange = (e) => {
    const { name, value } = e.target;
    setManualForm((current) => ({ ...current, [name]: value }));
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    setManualSaving(true);

    const name = manualForm.name.trim();
    const result = await submitProduct(manualForm, { apiBase: API });

    setManualSaving(false);

    if (!result.success) {
      toast.error(result.error);
      return;
    }

    toast.success(`"${name}" added to the catalog`);
    setManualForm(initialManualForm);
    fetchProducts();
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || "",
      description: product.description || "",
      source_url: product.source_url || "",
      marketplace: product.marketplace || "other",
      source_price: product.source_price === null ? "" : String(product.source_price),
      source_currency: product.source_currency || "USD",
      category: product.category || "Electronics",
      weight_lbs: product.weight_lbs === null ? "" : String(product.weight_lbs),
      estimated_weight_lbs:
        product.estimated_weight_lbs === null ? "" : String(product.estimated_weight_lbs),
      images: Array.isArray(product.images) ? product.images : [],
      in_stock: product.in_stock !== false,
      // Carried through the edit form so saving does not wipe fields the dialog
      // has no input for.
      status: product.status || "active",
      profit_margin: product.profit_margin === null ? "" : String(product.profit_margin),
    });
    setDialogOpen(true);
  };

  const handleDelete = async (productId) => {
    if (!confirm("Are you sure you want to delete this product?")) return;

    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        withCredentials: true,
      });
      toast.success("Product deleted");
      fetchProducts();
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error(extractApiErrorMessage(error, "Failed to delete product"));
    }
  };

  const filteredProducts = products.filter((p) =>
    matchesSearch([p.name, p.marketplace, p.category, p.product_id], search)
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {loadError && (
          <div
            className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4"
            data-testid="admin-products-load-error"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
            <div className="flex-1">
              <p className="font-medium text-red-700">Could not load the product list</p>
              <p className="text-sm text-red-600">{loadError}</p>
            </div>
            <Button size="sm" variant="outline" onClick={fetchProducts}>
              Retry
            </Button>
          </div>
        )}

        {costsRedacted && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Showing the public catalog feed — source costs are redacted there, so the price column
            falls back to landed pricing. Sign in again if edits need original cost data.
          </div>
        )}

        {/* Loved on site — admin picks from anywhere */}
        <div
          className={`rounded-lg border border-orange-200 bg-orange-50/50 p-4 ${
            lovedAvailable ? "" : "hidden"
          }`}
        >
          <div className="flex items-center gap-2 mb-3">
            <Heart className="h-5 w-5 text-orange-600" />
            <h2 className="font-semibold text-slate-900">Loved on CheepSweepz</h2>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Products with the most views, cart adds, and purchases anywhere on the site. Import any of them to the public showcase.
          </p>
          {lovedLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : lovedProducts.length === 0 ? (
            <p className="text-sm text-slate-500">No engagement data yet — showcase fills as customers browse and buy.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {lovedProducts.map((product) => (
                <div
                  key={product.product_id}
                  className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-md p-3"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{product.name}</p>
                    <p className="text-xs text-slate-500">
                      score {product.engagement_score ?? 0}
                      {product.is_showcased ? " · on showcase" : ""}
                    </p>
                  </div>
                  {!product.is_showcased && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={importingId === product.product_id}
                      onClick={() => handleImportToShowcase(product)}
                    >
                      {importingId === product.product_id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Sparkles className="h-3 w-3 mr-1" />
                          Showcase
                        </>
                      )}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="admin-products-title">
              Products
            </h1>
            <p className="text-slate-600">
              Curate the public showcase — import products customers love from anywhere on the site
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <AdminUrlSubmitAction onSubmitted={fetchProducts} />
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  onClick={() => {
                    setEditingProduct(null);
                    setFormData(initialProductForm);
                    setScrapeUrl("");
                  }}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="add-product-button"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingProduct ? "Edit Product" : "Add New Product"}
                </DialogTitle>
              </DialogHeader>

              {/* URL Scraper */}
              {!editingProduct && (
                <div className="p-4 bg-slate-50 rounded-sm mb-4">
                  <Label className="text-sm font-medium mb-2 block">
                    Import from URL (Optional)
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={scrapeUrl}
                      onChange={(e) => setScrapeUrl(e.target.value)}
                      placeholder="https://amazon.com/product/..."
                      data-testid="scrape-url-input"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleScrapeUrl}
                      disabled={scraping}
                      data-testid="scrape-button"
                    >
                      {scraping ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <LinkIcon className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="name">Product Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      data-testid="product-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="marketplace">Marketplace</Label>
                    <Select
                      value={formData.marketplace}
                      onValueChange={(v) => handleSelectChange("marketplace", v)}
                    >
                      <SelectTrigger data-testid="marketplace-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {marketplaces.map((mp) => (
                          <SelectItem key={mp} value={mp}>
                            {mp.charAt(0).toUpperCase() + mp.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(v) => handleSelectChange("category", v)}
                    >
                      <SelectTrigger data-testid="category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="source_price">Source Price (USD) *</Label>
                    <Input
                      id="source_price"
                      name="source_price"
                      type="number"
                      step="0.01"
                      value={formData.source_price}
                      onChange={handleInputChange}
                      required
                      data-testid="price-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="weight_lbs">Weight (lbs)</Label>
                    <Input
                      id="weight_lbs"
                      name="weight_lbs"
                      type="number"
                      step="0.1"
                      value={formData.weight_lbs}
                      onChange={handleInputChange}
                      placeholder="Actual weight"
                      data-testid="weight-input"
                    />
                  </div>

                  <div className="col-span-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={3}
                      data-testid="description-input"
                    />
                  </div>

                  <div className="col-span-2">
                    <Label htmlFor="source_url">Source URL</Label>
                    <Input
                      id="source_url"
                      name="source_url"
                      value={formData.source_url}
                      onChange={handleInputChange}
                      placeholder="https://..."
                      data-testid="source-url-input"
                    />
                  </div>

                  <div className="col-span-2">
                    <Label htmlFor="images">Image URLs (one per line)</Label>
                    <Textarea
                      id="images"
                      value={formData.images.join("\n")}
                      onChange={handleImageChange}
                      rows={3}
                      placeholder="https://image1.jpg&#10;https://image2.jpg"
                      data-testid="images-input"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="save-product-button"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : editingProduct ? (
                      "Update Product"
                    ) : (
                      "Create Product"
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Manual entry bypass — works even when scraping or imports are down */}
        <form
          onSubmit={handleManualSubmit}
          className="rounded-lg border border-slate-200 bg-white p-4"
          data-testid="manual-product-form"
        >
          <div className="mb-3 flex items-center gap-2">
            <PencilLine className="h-5 w-5 text-slate-500" />
            <h2 className="font-semibold text-slate-900">Manual entry</h2>
          </div>
          <p className="mb-4 text-sm text-slate-600">
            Add a product by hand when the scraper or marketplace import is unavailable. Landed
            pricing is calculated from the source cost and weight.
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="md:col-span-2">
              <Label htmlFor="manual-name">Title *</Label>
              <Input
                id="manual-name"
                name="name"
                value={manualForm.name}
                onChange={handleManualChange}
                placeholder="Manual Test Item"
                data-testid="manual-product-name"
              />
            </div>
            <div>
              <Label htmlFor="manual-price">Source Price (USD) *</Label>
              <Input
                id="manual-price"
                name="source_price"
                type="number"
                step="0.01"
                min="0"
                value={manualForm.source_price}
                onChange={handleManualChange}
                placeholder="99.99"
                data-testid="manual-product-price"
              />
            </div>
            <div>
              <Label htmlFor="manual-weight">Est. Weight (lbs)</Label>
              <Input
                id="manual-weight"
                name="estimated_weight_lbs"
                type="number"
                step="0.1"
                min="0"
                value={manualForm.estimated_weight_lbs}
                onChange={handleManualChange}
                placeholder="1.0"
                data-testid="manual-product-weight"
              />
            </div>
            <div>
              <Label htmlFor="manual-margin">Profit Margin (%)</Label>
              <Input
                id="manual-margin"
                name="profit_margin"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={manualForm.profit_margin}
                onChange={handleManualChange}
                placeholder="15"
                data-testid="manual-product-margin"
              />
            </div>
            <div>
              <Label htmlFor="manual-status">Status</Label>
              <Select
                value={manualForm.status}
                onValueChange={(v) => setManualForm((c) => ({ ...c, status: v }))}
              >
                <SelectTrigger id="manual-status" data-testid="manual-product-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRODUCT_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="manual-description">Description</Label>
              <Textarea
                id="manual-description"
                name="description"
                rows={2}
                value={manualForm.description}
                onChange={handleManualChange}
                placeholder="Product details created via manual entry"
                data-testid="manual-product-description"
              />
            </div>
            <div>
              <Label htmlFor="manual-category">Category</Label>
              <Select
                value={manualForm.category}
                onValueChange={(v) => setManualForm((c) => ({ ...c, category: v }))}
              >
                <SelectTrigger id="manual-category" data-testid="manual-product-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                type="submit"
                disabled={manualSaving}
                className="w-full bg-slate-900 hover:bg-slate-800"
                data-testid="manual-product-submit"
              >
                {manualSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Add Product"
                )}
              </Button>
            </div>
          </div>
        </form>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products..."
            className="pl-10"
            data-testid="search-products-input"
          />
        </div>

        {/* Products Table */}
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Product</TableHead>
                <TableHead>Marketplace</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6}>
                      <Skeleton className="h-12 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : filteredProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <Package className="h-12 w-12 mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-500">No products found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredProducts.map((product) => (
                  <TableRow key={product.product_id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-sm overflow-hidden">
                          {product.images?.[0] ? (
                            <img
                              src={product.images[0]}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Package className="w-full h-full p-2 text-slate-300" />
                          )}
                        </div>
                        <div className="max-w-[200px]">
                          <p className="font-medium truncate">{product.name}</p>
                          <p className="text-xs text-slate-500 mono truncate">
                            {product.product_id}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="capitalize">
                        {product.marketplace || "unknown"}
                      </Badge>
                    </TableCell>
                    <TableCell>{product.category || "—"}</TableCell>
                    <TableCell className="text-right font-medium">
                      {product.source_price === null ? (
                        <span
                          className="text-slate-400"
                          title="Source cost hidden by the public catalog response"
                        >
                          {product.landed_price === null
                            ? "—"
                            : `${formatMoney(product.landed_price)} landed`}
                        </span>
                      ) : (
                        formatMoney(product.source_price, product.source_currency)
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={product.in_stock ? "default" : "secondary"}
                        className={
                          product.in_stock
                            ? "bg-green-100 text-green-700"
                            : "bg-slate-100 text-slate-500"
                        }
                      >
                        {product.in_stock ? "In Stock" : "Out of Stock"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(product)}
                          data-testid={`edit-${product.product_id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(product.product_id)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`delete-${product.product_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </AdminLayout>
  );
}
